import { Octokit } from '@octokit/rest';
import * as yaml from 'js-yaml';
import { Prompt, GitHubConfig } from '../types';
import { decodeUtf8Base64, encodeUtf8Base64 } from './base64';
import { GitHubTokenManager } from './secureStorage';

export class GitHubPromptManager {
  private octokit: Octokit | null = null;
  private config: GitHubConfig;

  constructor(config: GitHubConfig) {
    this.config = config;
    // Octokit will be initialized on first use with token from secureStorage
  }

  private async getOctokit(): Promise<Octokit> {
    if (this.octokit) {
      return this.octokit;
    }
    
    const token = await GitHubTokenManager.getAccessToken();
    if (!token) {
      throw new Error('No GitHub token available. Please log in again.');
    }
    
    this.octokit = new Octokit({
      auth: token,
    });
    
    return this.octokit;
  }

  private sanitizeContentForYaml(content: string): string {
    // The previous ASCII folding and mojibake replacements only existed to
    // paper over Latin-1 base64 decoding. UTF-8 decoding keeps emoji, quotes,
    // and accents intact. YAML still rejects most C0 controls, so drop those.
    // Keep tab, LF, and CR.
    // eslint-disable-next-line no-control-regex
    return content.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  }

  async listPrompts(path = 'prompts'): Promise<Array<{ name: string; path: string; tags: string[] }>> {
    try {
      const octokit = await this.getOctokit();
      const promptPaths: Array<{ name: string; path: string }> = [];
      
      const collectPrompts = async (currentPath: string): Promise<void> => {
        try {
          const { data } = await octokit.rest.repos.getContent({
            owner: this.config.owner,
            repo: this.config.repo,
            path: currentPath,
          });

          if (!Array.isArray(data)) {
            return;
          }

          for (const item of data) {
            if (item.type === 'file' && item.name.endsWith('.yaml')) {
              // Get a more readable name by using the directory structure
              const pathParts = item.path.split('/');
              const fileName = item.name.replace('.yaml', '');
              const category = pathParts[pathParts.length - 2]; // parent directory name
              const displayName = category ? `${category}/${fileName}` : fileName;
              
              promptPaths.push({
                name: displayName,
                path: item.path,
              });
            } else if (item.type === 'dir') {
              // Recursively search subdirectories
              await collectPrompts(item.path);
            }
          }
        } catch (error) {
          console.error(`Error accessing path ${currentPath}:`, error);
          // Continue with other paths even if one fails
        }
      };

      await collectPrompts(path);

      // Load tags for each prompt so the list can filter by tag
      const prompts = await Promise.all(
        promptPaths.map(async (item) => {
          try {
            const prompt = await this.getPrompt(item.path);
            return {
              ...item,
              tags: Array.isArray(prompt.metadata?.tags) ? prompt.metadata.tags : [],
            };
          } catch (error) {
            console.error(`Error loading tags for ${item.path}:`, error);
            return { ...item, tags: [] as string[] };
          }
        })
      );
      
      return prompts.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error listing prompts:', error);
      throw new Error('Failed to list prompts from GitHub');
    }
  }

  async getPrompt(path: string): Promise<Prompt> {
    try {
      const octokit = await this.getOctokit();
      const { data } = await octokit.rest.repos.getContent({
        owner: this.config.owner,
        repo: this.config.repo,
        path,
      });

      if (Array.isArray(data) || data.type !== 'file') {
        throw new Error('Invalid file type');
      }

      let content = decodeUtf8Base64(data.content);
      
      // Clean up problematic Unicode characters that cause YAML parsing issues
      content = this.sanitizeContentForYaml(content);
      
      const prompt = yaml.load(content, { 
        // Allow Unicode characters but handle them properly
        schema: yaml.DEFAULT_SCHEMA,
        // More lenient parsing
        json: true 
      }) as Prompt;
      
      return prompt;
    } catch (error) {
      console.error('Error fetching prompt:', error);
      throw new Error('Failed to fetch prompt from GitHub');
    }
  }

  async savePrompt(path: string, prompt: Prompt): Promise<void> {
    try {
      const octokit = await this.getOctokit();
      const content = yaml.dump(prompt, { 
        lineWidth: -1,
        noRefs: true,
        sortKeys: false 
      });
      
      let sha: string | undefined;
      
      // Try to get existing file SHA for updates
      try {
        const { data } = await octokit.rest.repos.getContent({
          owner: this.config.owner,
          repo: this.config.repo,
          path,
        });
        
        if (!Array.isArray(data) && data.type === 'file') {
          sha = data.sha;
        }
      } catch {
        // File doesn't exist, will create new
      }

      await octokit.rest.repos.createOrUpdateFileContents({
        owner: this.config.owner,
        repo: this.config.repo,
        path,
        message: sha ? `Update prompt: ${prompt.metadata.title}` : `Create prompt: ${prompt.metadata.title}`,
        content: encodeUtf8Base64(content),
        sha,
      });
    } catch (error) {
      console.error('Error saving prompt:', error);
      throw new Error('Failed to save prompt to GitHub');
    }
  }

  async deletePrompt(path: string): Promise<void> {
    try {
      const octokit = await this.getOctokit();
      const { data } = await octokit.rest.repos.getContent({
        owner: this.config.owner,
        repo: this.config.repo,
        path,
      });

      if (Array.isArray(data) || data.type !== 'file') {
        throw new Error('File not found');
      }

      await octokit.rest.repos.deleteFile({
        owner: this.config.owner,
        repo: this.config.repo,
        path,
        message: `Delete prompt: ${path}`,
        sha: data.sha,
      });
    } catch (error) {
      console.error('Error deleting prompt:', error);
      throw new Error('Failed to delete prompt from GitHub');
    }
  }

  async searchPrompts(query: string): Promise<Array<{ name: string; path: string }>> {
    try {
      const octokit = await this.getOctokit();
      const { data } = await octokit.rest.search.code({
        q: `${query} repo:${this.config.owner}/${this.config.repo} extension:yaml`,
      });

      return data.items.map(item => ({
        name: item.name.replace('.yaml', ''),
        path: item.path,
      }));
    } catch (error) {
      console.error('Error searching prompts:', error);
      throw new Error('Failed to search prompts');
    }
  }
}